// Drag-to-resize for the sidebar and the artifact drawer.
//
// Deliberately hand-rolled on pointer events rather than pulling in a panel
// library for two handles — nothing on npm earns its weight here.
//
// The handle is a real role="separator" with arrow-key support, so resizing is
// not mouse-only.

import { useCallback, useEffect, useRef, useState } from "react";

/** Never let a panel eat the workspace, however hard someone drags. */
const MAX_VIEWPORT_FRACTION = 0.6;
/** Arrow-key step; Shift multiplies it. */
const KEY_STEP = 16;

export interface ResizablePanelOptions {
  /** localStorage key, following the `eryndal.` convention already in use. */
  storageKey: string;
  defaultWidth: number;
  min: number;
  max: number;
  /**
   * Which edge the handle lives on. A left-hand panel grows as the pointer
   * moves right; a right-hand panel grows as it moves left.
   */
  side: "left" | "right";
}

function readStored(key: string, fallback: number): number {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  } catch {
    // Private mode, blocked site data — a remembered width is a convenience,
    // never a requirement.
    return fallback;
  }
}

export function useResizablePanel({
  storageKey,
  defaultWidth,
  min,
  max,
  side,
}: ResizablePanelOptions) {
  const [width, setWidth] = useState(defaultWidth);
  const [isDragging, setIsDragging] = useState(false);
  const widthRef = useRef(width);

  const clamp = useCallback(
    (next: number) => {
      const ceiling = Math.min(max, Math.round(window.innerWidth * MAX_VIEWPORT_FRACTION));
      return Math.max(min, Math.min(ceiling, Math.round(next)));
    },
    [min, max],
  );

  // Read the stored width after mount rather than in useState, so server-side
  // and first-paint markup stay predictable.
  useEffect(() => {
    const stored = clamp(readStored(storageKey, defaultWidth));
    widthRef.current = stored;
    setWidth(stored);
  }, [storageKey, defaultWidth, clamp]);

  const commit = useCallback(
    (next: number) => {
      const clamped = clamp(next);
      widthRef.current = clamped;
      setWidth(clamped);
      try {
        window.localStorage.setItem(storageKey, String(clamped));
      } catch {
        /* storage unavailable; the width still applies for this session */
      }
    },
    [clamp, storageKey],
  );

  // A narrowing viewport must not leave a panel wider than the rule allows.
  useEffect(() => {
    const onResize = () => commit(widthRef.current);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [commit]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = widthRef.current;
      const handle = e.currentTarget;
      handle.setPointerCapture(e.pointerId);
      setIsDragging(true);

      const onMove = (ev: PointerEvent) => {
        const delta = ev.clientX - startX;
        commit(startWidth + (side === "left" ? delta : -delta));
      };
      const onUp = (ev: PointerEvent) => {
        handle.releasePointerCapture(ev.pointerId);
        handle.removeEventListener("pointermove", onMove);
        handle.removeEventListener("pointerup", onUp);
        handle.removeEventListener("pointercancel", onUp);
        setIsDragging(false);
      };

      handle.addEventListener("pointermove", onMove);
      handle.addEventListener("pointerup", onUp);
      handle.addEventListener("pointercancel", onUp);
    },
    [commit, side],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? KEY_STEP * 4 : KEY_STEP;
      // Left/Right are screen directions; which one widens depends on the side.
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        commit(widthRef.current + (side === "left" ? -step : step));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        commit(widthRef.current + (side === "left" ? step : -step));
      } else if (e.key === "Home") {
        e.preventDefault();
        commit(defaultWidth);
      }
    },
    [commit, side, defaultWidth],
  );

  return {
    width,
    isDragging,
    /** Spread onto the grab handle. */
    separatorProps: {
      role: "separator" as const,
      "aria-orientation": "vertical" as const,
      "aria-valuenow": width,
      "aria-valuemin": min,
      "aria-valuemax": max,
      tabIndex: 0,
      onPointerDown,
      onKeyDown,
      onDoubleClick: () => commit(defaultWidth),
    },
  };
}
